<script setup lang="ts">
/**
 * GET /models/{modelId}/held-out-evaluation: the bounded held-out measurement as answer pairs.
 * Each pair shows the question, the answer the training set holds, and what the untouched base
 * model and this version answered, each marked right or wrong; pages follow nextCursor.
 */
import type { components } from '@scalty/contracts'
import Dialog from '~/components/ui/Dialog.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import ActionLink from '~/components/ui/ActionLink.vue'
import StatusCell from '~/components/ui/StatusCell.vue'

type Sample = components['schemas']['HeldOutEvaluationSample']
defineProps<{ model: string; summary: string; samples: Sample[]; pending: boolean; more: boolean; loadingMore: boolean; failure: string | null }>()
const emit = defineEmits<{ close: []; more: [] }>()
const SET: Record<Sample['set'], string> = { seen_phrasings: 'seen phrasing', unseen_passages: 'unseen passage' }
const DIRECTION: Record<string, string> = { subject_to_value: 'asked forward', value_to_subject: 'asked in reverse' }
</script>

<template>
  <Dialog title="Questions kept back from training" :meta="model" :width="760" @close="emit('close')">
    <template #icon><Icon name="compare" /></template>
    <p v-if="failure" role="alert" class="m-0 text-xs text-destructive">{{ failure }}</p>
    <p v-else-if="pending" class="m-0 text-xs text-muted-foreground">Reading the answer pairs…</p>
    <p v-else-if="!samples.length" class="m-0 text-xs text-muted-foreground">No answer pairs were retained for this version.</p>
    <template v-else>
      <p class="num m-0 text-[length:var(--type-meta)] text-muted-foreground">{{ summary }}</p>
      <div class="flex max-h-[var(--measure-400px)] flex-col gap-1.5 overflow-auto">
        <div v-for="(sample, index) in samples" :key="index" class="flex flex-col gap-1.5 rounded-md border border-border p-2.5 text-xs">
          <div class="flex items-start gap-2">
            <span class="min-w-0 flex-1 whitespace-pre-wrap break-words text-foreground">{{ sample.question }}</span>
            <span class="num shrink-0 text-[length:var(--type-fine)] text-muted-foreground">{{ SET[sample.set] }}<template v-if="sample.refusal"> · out of scope</template><template v-else-if="sample.direction && DIRECTION[sample.direction]"> · {{ DIRECTION[sample.direction] }}</template></span>
          </div>
          <div class="flex items-start gap-2"><span class="num w-16 shrink-0 text-muted-foreground">Expected</span><span class="min-w-0 whitespace-pre-wrap break-words text-foreground">{{ sample.refusal ? 'The fixed refusal — the question is outside the documents' : sample.reference }}</span></div>
          <div class="grid gap-2 md:grid-cols-2">
            <div class="flex flex-col gap-1 rounded-[var(--radius-compact)] border border-border/60 p-2">
              <div class="flex items-center justify-between gap-2"><span class="num shrink-0 text-muted-foreground">Base model</span><StatusCell :status="sample.baseCorrect ? 'Ready' : 'Failed'" :label="sample.baseCorrect ? 'Right' : 'Wrong'" /></div>
              <span class="whitespace-pre-wrap break-words text-foreground">{{ sample.baseAnswer }}</span>
              <span v-if="sample.baseConfusion" class="num text-[length:var(--type-fine)] text-muted-foreground">answered with the neighbouring cell · {{ sample.baseConfusion }}</span>
            </div>
            <div class="flex flex-col gap-1 rounded-[var(--radius-compact)] border border-border/60 p-2">
              <div class="flex items-center justify-between gap-2"><span class="num shrink-0 text-muted-foreground">This version</span><StatusCell :status="sample.tunedCorrect ? 'Ready' : 'Failed'" :label="sample.tunedCorrect ? 'Right' : 'Wrong'" /></div>
              <span class="whitespace-pre-wrap break-words text-foreground">{{ sample.tunedAnswer }}</span>
              <span v-if="sample.tunedConfusion" class="num text-[length:var(--type-fine)] text-muted-foreground">answered with the neighbouring cell · {{ sample.tunedConfusion }}</span>
            </div>
          </div>
        </div>
        <div v-if="more" class="flex h-8 shrink-0 items-center justify-center">
          <ActionLink :label="loadingMore ? 'Reading…' : 'Show more pairs'" :disabled="loadingMore" @click="emit('more')"><template #icon><Icon name="chevron-down" /></template></ActionLink>
        </div>
      </div>
      <p class="num m-0 text-[length:var(--type-fine)] text-muted-foreground">{{ samples.length }} pairs shown{{ more ? ' · more on the server' : '' }}</p>
    </template>
    <template #actions>
      <ActionButton label="Close" @click="emit('close')" />
    </template>
  </Dialog>
</template>
